<template>
  <div>
    <section class="container page-top hero">
      <span class="pill">{{ page.hero.eyebrow }}</span>
      <h1 class="h-title">{{ page.hero.headline }}</h1>
      <p class="lead">{{ page.hero.body }}</p>
      <p v-if="isAr" class="note">{{ ui('language_note') }}</p>
    </section>

    <section class="section container mission">
      <div class="mission-text">
        <h2 class="h2 heading">{{ page.mission.heading }}</h2>
        <p class="lead">{{ page.mission.lead }}</p>
        <p class="body">{{ page.mission.body }}</p>
      </div>
      <div class="values">
        <article v-for="(value, index) in page.values" :key="value.title" class="value">
          <span class="chip" :style="{ background: chips[index % chips.length] }" aria-hidden="true"></span>
          <h3 class="card-title value-title">{{ value.title }}</h3>
          <p class="value-body">{{ value.body }}</p>
        </article>
      </div>
    </section>

    <section class="section container">
      <div class="section-head">
        <h2 class="h2 heading">{{ page.team_heading.title }}</h2>
        <p class="lead">{{ page.team_heading.body }}</p>
      </div>
      <div class="team">
        <article v-for="(member, index) in team" :key="member.id" class="member">
          <img v-if="member.photo" class="avatar" :src="member.photo" :alt="member.name">
          <span v-else class="avatar initials" :style="{ background: avatarTints[(index + 1) % avatarTints.length] }" aria-hidden="true">{{ member.initials }}</span>
          <h3 class="card-title member-name">{{ member.name }}</h3>
          <span class="role">{{ member.role }}</span>
          <p class="bio">{{ member.bio }}</p>
        </article>
      </div>
    </section>

    <section v-if="milestones.length" class="section container">
      <div class="on-dark-panel">
        <h2 class="h2 heading journey">{{ page.timeline_heading.title }}</h2>
        <ol class="timeline">
          <li v-for="(milestone, index) in milestones" :key="index" class="milestone">
            <span class="year">{{ milestone.year }}</span>
            <div>
              <h3 class="m-title">{{ milestone.title }}</h3>
              <p class="m-body">{{ milestone.description }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <CtaBand />
  </div>
</template>

<script setup>
import CtaBand from '../components/CtaBand.vue'
import { useLocale } from '../composables/useLocale'
import { team, milestones, pages } from '../data'

const page = pages.about
const { isAr, ui } = useLocale()

const chips = ['var(--chip-1)', 'var(--chip-2)', 'var(--chip-3)', 'var(--chip-4)', 'var(--chip-5)', 'var(--chip-6)']
// Initials are navy, so avatars skip the navy chip.
const avatarTints = chips.slice(0, 5)
</script>

<style scoped>
.hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
}

.hero .lead {
  max-width: 56ch;
}

.mission {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 440px), 1fr));
  gap: clamp(32px, 5vw, 72px);
  align-items: start;
}

.heading {
  font-size: clamp(32px, 4vw, 52px);
  margin-bottom: 20px;
}

.mission-text .body {
  margin-top: 16px;
  font-size: 17px;
  line-height: 1.7;
  color: var(--muted);
}

.values {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 16px;
}

.value {
  background: var(--surface);
  border-radius: var(--radius-card);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chip {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-chip);
  margin-bottom: 12px;
}

.value-title {
  font-size: 26px;
}

.value-body {
  font-size: 16px;
  line-height: 1.55;
  color: var(--muted);
}

.section-head {
  max-width: 640px;
  margin-bottom: 40px;
}

.team {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: 16px;
}

.member {
  background: var(--surface);
  border-radius: var(--radius-card);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 12px;
}

.initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-size: 26px;
  color: var(--ink);
}

.member-name {
  font-size: 26px;
}

.role {
  font-size: 14px;
  color: var(--tertiary);
}

.bio {
  margin-top: 8px;
  font-size: 16px;
  line-height: 1.55;
  color: var(--muted);
}

.journey {
  margin-bottom: 32px;
}

.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}

.milestone {
  display: grid;
  grid-template-columns: minmax(80px, 140px) 1fr;
  gap: 24px;
  padding: 24px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
}

.year {
  font-family: 'Newsreader', serif;
  font-style: italic;
  font-size: clamp(28px, 4vw, 44px);
  line-height: 1;
  color: var(--gold-on-dark);
}

.m-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 26px;
  line-height: 1.2;
  margin-bottom: 8px;
}

.m-body {
  color: var(--on-dark-muted);
  line-height: 1.6;
}
</style>
